import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Divider, IconButton } from "@material-ui/core";
import { FilterList, VisibilityOutlined } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import StickyHeadTable from "src/components/CommonComponents/Table";
import CommonFilter2 from "src/components/CommonComponents/CommonFilter2";
import LoadingBox from "src/components/LoadingBox";
import DialogView from "src/components/CommonComponents/DialogView";
import ViewForm from "src/views/sakhi_transactions/sakhi_transactions_view/ViewForm";
import theme from "src/theme";
import { fetchWrapper } from "src/services/http_requests";
import { paramsUrl } from "src/utils/halper";
import { generateSerialNo } from "src/utils";
import { purposeListAction } from "src/Redux/Actions/PurposeAction";
import { stateListAction } from "src/Redux/Actions/StateAction";
import { ngoListAction } from "src/Redux/Actions/NgoAction";
import { toast } from "react-toastify";

const columns = [
  { id: "sno", label: "S.No" },
  { id: "id", label: "ID" },
  { id: "purpose", label: "Purpose" },
  { id: "beneficiary", label: "Beneficiary" },
  { id: "fo", label: "FO Name" },
  { id: "sakhi", label: "Sakhi" },
  { id: "meeting", label: "Meeting Date", minWidth: 10 },
  { id: "block", label: "Block" },
  { id: "village", label: "Village" },
  { id: "schema", label: "Scheme" },
  { id: "status", label: "Status" },
  { id: "action", label: "Action" },
];

const emptyFilter = {
  state_id: "",
  block_id: "",
  village_id: "",
  purpose_id: "",
  district_id: "",
  sakhi_id: "",
  status_id: "",
  from: "",
  to: "",
  year: "",
  ngo_id: "",
  fo_id: "",
  scheme_id: "",
};

function createData(
  sno,
  id,
  purpose,
  beneficiary,
  fo,
  sakhi,
  meeting,
  block,
  village,
  schema,
  status,
  action
) {
  return {
    sno,
    id,
    purpose,
    beneficiary,
    fo,
    sakhi,
    meeting,
    block,
    village,
    schema,
    status,
    action,
  };
}

function buildQuery(filters, pageNum) {
  return {
    state_id: filters?.state_id,
    district_id: filters?.district_id,
    block_id: filters?.block_id,
    village_id: filters?.village_id,
    purpose_id: filters?.purpose_id,
    scheme_id: filters?.scheme_id,
    ngo_id: filters?.ngo_id,
    fo_id: filters?.fo_id,
    sakhi_id: filters?.sakhi_id,
    status_id: filters?.status_id,
    from: filters.from ? moment(filters.from).format("YYYY-MM-DD") : "",
    to: filters.to ? moment(filters.to).format("YYYY-MM-DD") : "",
    year: filters?.year,
    page: pageNum,
  };
}

function BeneficiaryTransactionAllDataTab({
  initialData = [],
  initialTotal = 0,
  parentLoading = false,
  parentHasLoaded = false,
  parentPage = 1,
  setParentPage,
}) {
  const loginInfo = JSON.parse(localStorage.getItem("loginInfo")) || {};
  const loginUserRole = loginInfo?.data?.role_id;

  const [filterData, setFilterData] = useState(emptyFilter);
  const [showFilter, setShowFilter] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [localList, setLocalList] = useState([]);
  const [localTotal, setLocalTotal] = useState(0);
  const [localPage, setLocalPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [useLocalData, setUseLocalData] = useState(false);
  const [localHasLoaded, setLocalHasLoaded] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const { states } = useSelector((state) => state.statesList);
  const { purposes } = useSelector((state) => state.purposeList);
  const dispatch = useDispatch();

  const filterRef = useRef(filterData);
  filterRef.current = filterData;

  const list = useLocalData ? localList : initialData;
  const total = useLocalData ? localTotal : initialTotal;
  const loading = useLocalData ? localLoading : parentLoading;
  const hasLoaded = useLocalData ? localHasLoaded : parentHasLoaded;
  const page = useLocalData ? localPage : parentPage;

  const handlePageChange = (newPage) => {
    if (useLocalData) {
      setLocalPage(newPage);
    } else if (setParentPage) {
      setParentPage(newPage);
    }
  };

  const fetchList = useCallback(async (filters, pageNum) => {
    setLocalLoading(true);
    setLocalHasLoaded(false);
    try {
      const data = await fetchWrapper.get(
        `${process.env.REACT_APP_API_URL}/beneficiary_transaction/list/all-data${paramsUrl(
          buildQuery(filters, pageNum)
        )}`
      );
      const rows = data?.data ?? [];
      setLocalList(rows);
      if (data?.total) {
        setLocalTotal(data.total);
        localStorage.setItem("totalData", data.total);
      } else if (parseInt(pageNum, 10) === 1) {
        setLocalTotal(rows.length);
        localStorage.setItem("totalData", rows.length);
      } else {
        setLocalTotal(Number(localStorage.getItem("totalData")) || 0);
      }
    } catch (error) {
      if (error) toast.error(error);
    } finally {
      setLocalLoading(false);
      setLocalHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (purposes === undefined || purposes === null) {
      dispatch(purposeListAction({ fetched: "All" }));
    }
    if (states === undefined || states === null) {
      dispatch(stateListAction({ fetched: "All" }));
    }
    if (loginUserRole == 1) {
      dispatch(ngoListAction({ fetched: "All" }));
    }
  }, [dispatch, purposes, states, loginUserRole]);

  useEffect(() => {
    if (useLocalData) {
      fetchList(filterRef.current, localPage);
    }
  }, [localPage, fetchList, useLocalData]);

  const createStatus = (foStatus, ngoStatus) => {
    let status = "";
    const approve = "bg-green-200 text-green-500";
    const pending = "bg-yellow-200 text-yellow-500";
    const rejected = "bg-red-200 text-red-500";

    if (parseInt(loginUserRole, 10) === 1) {
      if (parseInt(foStatus, 10) === 1) {
        status = "Pending by FO";
      } else if (parseInt(foStatus, 10) === 2 && parseInt(ngoStatus, 10) === 1) {
        status = "Pending by NGO";
      } else if (parseInt(ngoStatus, 10) === 2) {
        status = "Approved by NGO";
      } else if (parseInt(foStatus, 10) === 3) {
        status = "Rejected by FO";
      } else if (parseInt(ngoStatus, 10) === 3) {
        status = "Rejected by NGO";
      } else {
        return null;
      }
    } else {
      if (parseInt(foStatus, 10) === 1) status = "Pending by FO";
      else if (parseInt(foStatus, 10) === 2 && parseInt(ngoStatus, 10) === 1) {
        status = "Pending";
      } else if (parseInt(foStatus, 10) === 2 && parseInt(ngoStatus, 10) === 2) {
        status = "Approved";
      } else if (parseInt(foStatus, 10) === 3 && parseInt(ngoStatus, 10) === 1) {
        status = "Rejected by FO";
      } else status = "Rejected";
    }
    const statusColor = status.includes("Approved")
      ? approve
      : status.includes("Pending")
        ? pending
        : rejected;

    return (
      <Box className={`p-1 rounded py-1.5 ${statusColor}`}>{status}</Box>
    );
  };

  const handleFilterSearch = () => {
    setUseLocalData(true);
    if (localPage === 1) {
      fetchList(filterData, 1);
    } else {
      localStorage.setItem("pageInfo", 1);
      setLocalPage(1);
    }
  };

  const handleRestFilter = () => {
    setFilterData({ ...emptyFilter });
    setUseLocalData(true);
    if (localPage !== 1) {
      localStorage.setItem("pageInfo", 1);
      setLocalPage(1);
    } else {
      fetchList({ ...emptyFilter }, 1);
    }
  };

  const handleViewTransaction = async (id) => {
    try {
      const details = await fetchWrapper.get(
        `${process.env.REACT_APP_API_URL}/beneficiary_transaction/list${paramsUrl({
          id,
          fetched: "All",
        })}`
      );
      const selectedData = details?.data?.[0];

      if (!selectedData) {
        toast.error("Transaction details not found");
        return;
      }

      setSelectedTransaction(selectedData);
      setOpenView(true);
    } catch (error) {
      if (error) toast.error(error);
    }
  };

  const filtersToDisplay = [
    "state_id",
    "district_id",
    "block_id",
    "village_id",
    "purpose_id",
    "scheme_id",
    "status_id",
    "from",
    "to",
    "year",
    "fo_id",
    "sakhi_id",
  ];

  const rows = list?.map((item, i) =>
    createData(
      generateSerialNo(i, page),
      item?.id,
      item?.purpose_name,
      item?.ben_name,
      item?.fo_name,
      item?.sakhi_name,
      item?.meeting_date ? moment(item.meeting_date).format("DD/MM/YYYY") : "",
      item?.block,
      item?.village,
      item?.scheme,
      createStatus(item.is_approve_status_fo, item.is_approve_status_ngo),
      <IconButton
        size="small"
        onClick={() => handleViewTransaction(item?.id)}
      >
        <VisibilityOutlined color="primary" fontSize="small" />
      </IconButton>
    )
  );

  return (
    <Box width="100%">
      <Box
        pb={2}
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="flex-end"
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<FilterList color="primary" />}
          onClick={() => setShowFilter(!showFilter)}
          style={{
            backgroundColor: "white",
            color: theme.palette.primary.main,
          }}
        >
          Filter
        </Button>
      </Box>
      <Divider style={{ maxWidth: "100%" }} />
      {showFilter ? (
        <CommonFilter2
          filterData={filterData}
          showFilter={showFilter}
          setFilterData={setFilterData}
          handleFilterSearch={handleFilterSearch}
          handleRestFilter={handleRestFilter}
          filtersToDisplay={
            loginUserRole == 1
              ? [...filtersToDisplay, "ngo_id"]
              : filtersToDisplay
          }
          parentComponent="sakhi_transactions"
        />
      ) : null}
      {loading ? (
        <LoadingBox type={showFilter ? "table" : ""} />
      ) : (
        <StickyHeadTable
          columns={columns}
          rows={rows}
          loading={loading}
          showEmptyState={!loading && hasLoaded}
          total={total}
          page={page && page}
          setPage={handlePageChange}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <DialogView
        openView={openView}
        handleCloseView={() => setOpenView(false)}
        title="View Transaction"
      >
        <ViewForm
          sakhiDetails={selectedTransaction}
          crudAction={{ create: false, edit: false, view: true, delete: false }}
          isReadOnlyFromReport={true}
        />
      </DialogView>
    </Box>
  );
}

export default BeneficiaryTransactionAllDataTab;
